import type { MessageInstance, JointContent } from 'antd/lib/message/interface'
import type { FilterGroup } from 'src/utils/common'
import type { UserPerm, RolePermForms } from './declare'
import React from 'react'
import { appStore } from 'src/stores'
import { $consts, $evt } from 'src/plugins'
import { getSingleRule } from 'src/utils/common'
import { getFormatMoment } from '../common/helper'
import { isEmpty, getQueryParams } from 'src/utils/tools'
import { get, set, uniq, cloneDeep } from 'lodash'
import dayjs from 'dayjs'

/**
 * 用于在多层弹框中，保持 message 不被 Modal 遮挡
 * @param msg 
 * @param field 
 */
export const postMsg = (msg: JointContent | React.Key, type: keyof MessageInstance = 'success') => {
  $evt.emit($consts['EVT/POST_MESSAGE'], msg, type)
}

/**
 * 获取表单权限
 * @param formId 
 * @returns 
 */
export const getFormPermission = (formId: string): boolean | RolePermForms => {
  const userPerm: UserPerm = appStore.current.userPerm

  // 没有配置权限，则默认有全部权限
  if (!userPerm.Nav_RolePerm) return true

  const activeMenu = appStore.current.activeMenu ?? {}
  const targetPerm = userPerm.Nav_RolePerm.Nav_RolePermForms.filter(perm => perm.MenuId === activeMenu.ID) ?? []
  return targetPerm.find(perm => perm.PermFormId === formId) ?? false
}

/**
 * 按钮权限
 * @param btnId 
 * @returns 
 */
export const useBtnPermission = (btnId: string): boolean => {
  const userPerm: UserPerm = appStore.current.userPerm

  // 没有配置权限，则默认有全部权限
  if (!userPerm.Nav_RolePerm) return true

  const validBtns: string[] = []
  userPerm.Nav_RolePerm.Nav_RolePermForms?.forEach(perm => {
    perm.Nav_Btns.forEach(btn => validBtns.push(btn.BtnId))
  })

  return validBtns.indexOf(btnId) !== -1
}

/**
 * 获取菜单配置参数
 * @param menu 
 */
export function getMenuFormParams (menu: any) {
  const { MENU_FORM_PARAMS } = menu
  if (!MENU_FORM_PARAMS) return {}

  const params = MENU_FORM_PARAMS.split('|')
  const formParam: any = {}
  params.forEach((param: any) => {
    const keyValues = param.split('=')
    formParam[keyValues[0]] = keyValues[1]
  })
  return formParam
}

/**
 * 获取表单配置的自定义参数，格式化成 {key:value} 形式
 * @param tables 
 * @param index 
 * @returns 
 */
export function getCustomParamsFromNav_Tables (tables: any[], index: number = 0) {
  return getQueryParams(tables?.[index]?.CUSTOM_PARAMS)
}

/**
 * 根据 Table Nav_Columns 获取 Include 查询过滤参数
 * @param Nav_Columns 
 * @returns 
 */
export const getNavFieldsFromNavColumns = (Nav_Columns: any[] = []) => {
  const navFields: string[] = []
  Nav_Columns.forEach((col: any) => {
    const { FIELD_NAME, CONTROL_TYPE, INPUT_LABEL_FIELD } = col
    const validField = getNavFieldsFromNavField(FIELD_NAME)
    validField.length && navFields.push(...validField)

    // 分页下拉多选的特殊 Include 参数
    if (CONTROL_TYPE === $consts['TABLE/CONTROL_TYPE_PAGINATION_MULTI_SELECT']) {
      navFields.push(FIELD_NAME)
      const validField = getNavFieldsFromNavField(INPUT_LABEL_FIELD)
      validField.length && navFields.push(`${FIELD_NAME}.${validField[0]}`)
    }
  })
  return uniq(navFields)
}

/**
 * 根据搜索控件的 DataType 获取查询参数对应的 operate
 * @param dataType 
 * @returns 
 */
export const getDataTypeMatchOperate = (dataType: number) => {
  switch (dataType) {
    case $consts['TABLE/DATA_TYPE_TEXTINPUT']:
      return $consts['TABLE/OPERATE_TYPE_INCLUDE']
    default:
      return $consts['TABLE/OPERATE_TYPE_EQUAL']
  }
}

/**
 * CONTROL_TYPE 时间控件类型转为通用定义的时间控件类型
 * @param CONTROL_TYPE 
 * @returns 
 */
export const CONTROL_TYPE_mapto_DATE_TYPE = (CONTROL_TYPE: number) => {
  switch (CONTROL_TYPE) {
    case $consts['TABLE/CONTROL_TYPE_DATE_SECOND']:
      return $consts['COMMON/DATE_TYPE_SECOND']
    case $consts['TABLE/CONTROL_TYPE_DATE_DAY_SECOND']:
      return $consts['COMMON/DATE_TYPE_DAY_SECOND']
    case $consts['TABLE/CONTROL_TYPE_DATE_RANGE_DAY_SECOND']:
      return $consts['COMMON/DATE_TYPE_RANGE_DAY_SECOND']
    case $consts['TABLE/CONTROL_TYPE_DATE_RANGE_DAY_MINUTE']:
      return $consts['COMMON/DATE_TYPE_RANGE_DAY_MINUTE']
    case $consts['TABLE/CONTROL_TYPE_DATE_DAY']:
      return $consts['COMMON/DATE_TYPE_DAY']
    case $consts['TABLE/CONTROL_TYPE_DATE_RANGE_DAY']:
      return $consts['COMMON/DATE_TYPE_RANGE_DAY']
    case $consts['TABLE/CONTROL_TYPE_DATE_MONTH']:
      return $consts['COMMON/DATE_TYPE_MONTH']
    case $consts['TABLE/CONTROL_TYPE_DATE_RANGE_MONTH']:
      return $consts['COMMON/DATE_TYPE_RANGE_MONTH']
    case $consts['TABLE/CONTROL_TYPE_DATE_YEAR']:
      return $consts['COMMON/DATE_TYPE_YEAR']
    case $consts['TABLE/CONTROL_TYPE_DATE_RANGE_YEAR']:
      return $consts['COMMON/DATE_TYPE_RANGE_YEAR']
    default:
      return $consts['COMMON/DATE_TYPE_DAY']
  }
}

/**
 * DATA_TYPE 时间控件类型转为通用定义的时间控件类型
 * @param DATA_TYPE 
 * @returns 
 */
export const DATA_TYPE_mapto_DATE_TYPE = (DATA_TYPE: number) => {
  switch (DATA_TYPE) {
    case $consts['TABLE/DATA_TYPE_DATE_RANGE_DAY_SECOND']:
      return $consts['COMMON/DATE_TYPE_RANGE_DAY_SECOND']
    case $consts['TABLE/DATA_TYPE_DATE_RANGE_DAY_MINUTE']:
      return $consts['COMMON/DATE_TYPE_RANGE_DAY_MINUTE']
    case $consts['TABLE/DATA_TYPE_DATE_DAY']:
      return $consts['COMMON/DATE_TYPE_DAY']
    case $consts['TABLE/DATA_TYPE_DATE_RANGE_DAY']:
      return $consts['COMMON/DATE_TYPE_RANGE_DAY']
    case $consts['TABLE/DATA_TYPE_DATE_MONTH']:
      return $consts['COMMON/DATE_TYPE_MONTH']
    case $consts['TABLE/DATA_TYPE_DATE_RANGE_MONTH']:
      return $consts['COMMON/DATE_TYPE_RANGE_MONTH']
    case $consts['TABLE/DATA_TYPE_DATE_YEAR']:
      return $consts['COMMON/DATE_TYPE_YEAR']
    case $consts['TABLE/DATA_TYPE_DATE_RANGE_YEAR']:
      return $consts['COMMON/DATE_TYPE_RANGE_YEAR']
    default:
      return $consts['COMMON/DATE_TYPE_DAY']
  }
}

/**
 * 获取时间控件配置默认值
 * @param DATA_VALUE 
 * @returns 
 */
export const getDateValueFrom_DATA_VALUE = (DATA_VALUE: string, DATA_TYPE: number) => {
  // {now,d,-7}{now,d,0} => ['now,d,-7', 'now,d,0']
  const regex = /(?<=\{)(.*?)(?=\})/g
  const match = DATA_VALUE.match(regex)
  if (match?.length) {
    // 如果是区间控件，则检查 match 的长度，如果只有一个，则需要将结束时间添加进去，结束时间为当前时间
    if (
      match.length === 1 &&
      (
        DATA_TYPE === $consts['TABLE/DATA_TYPE_DATE_RANGE_DAY_SECOND'] ||
        DATA_TYPE === $consts['TABLE/DATA_TYPE_DATE_RANGE_DAY_MINUTE'] ||
        DATA_TYPE === $consts['TABLE/DATA_TYPE_DATE_RANGE_DAY'] ||
        DATA_TYPE === $consts['TABLE/DATA_TYPE_DATE_RANGE_MONTH'] ||
        DATA_TYPE === $consts['TABLE/DATA_TYPE_DATE_RANGE_YEAR']
      )
    ) {
      const [now, type] = match[0].split(',')
      match.push(`${now},${type},0`)
    }
    // 根据参数生成时间
    const str: string[] = []
    match.forEach((item: string, index: number, arr: any[]) => {
      const isEndDate = index === arr.length - 1
      const [now, type, value] = item.split(',')
      if (now === 'now') {
        switch (type) {
          case 'd':
            str.push(getFormatMoment(dayjs().add(Number(value), 'days'), { dateType: DATA_TYPE_mapto_DATE_TYPE(DATA_TYPE), isEndDate }))
            break
          case 'M':
            str.push(getFormatMoment(dayjs().add(Number(value), 'months'), { dateType: DATA_TYPE_mapto_DATE_TYPE(DATA_TYPE), isEndDate }))
            break
          case 'y':
            str.push(getFormatMoment(dayjs().add(Number(value), 'years'), { dateType: DATA_TYPE_mapto_DATE_TYPE(DATA_TYPE), isEndDate }))
            break
          default:
            str.push(getFormatMoment(dayjs(), { dateType: DATA_TYPE_mapto_DATE_TYPE(DATA_TYPE), isEndDate }))
        }
      }
    })
    return str.join(',')
  }
  return DATA_VALUE
}

/**
 * 根据 ListSearch 搜索配置获取搜索条件默认值
 * @param DATA_VALUE ListSearch 搜索条件默认值
 * @param config  ListSearch 搜索配置
 */
export const getDefaultValueFrom_DATA_VALUE = (DATA_VALUE: any, config: { DATA_TYPE: number }) => {
  if (DATA_VALUE === undefined) return DATA_VALUE

  switch (config.DATA_TYPE) {
    // 数字输入框、单选下拉框
    case $consts['TABLE/DATA_TYPE_NUMBERINPUT']:
    case $consts['TABLE/DATA_TYPE_ENUM_SELECT']:
      return +DATA_VALUE
    // 多选下拉框
    case $consts['TABLE/DATA_TYPE_ENUM_MULTI_SELECT']:
      return DATA_VALUE.split(',').map((item: string) => +item)
    // 时间控件类型
    case $consts['TABLE/DATA_TYPE_DATE_RANGE_DAY_SECOND']:
    case $consts['TABLE/DATA_TYPE_DATE_RANGE_DAY_MINUTE']:
    case $consts['TABLE/DATA_TYPE_DATE_DAY']:
    case $consts['TABLE/DATA_TYPE_DATE_RANGE_DAY']:
    case $consts['TABLE/DATA_TYPE_DATE_MONTH']:
    case $consts['TABLE/DATA_TYPE_DATE_RANGE_MONTH']:
    case $consts['TABLE/DATA_TYPE_DATE_YEAR']:
    case $consts['TABLE/DATA_TYPE_DATE_RANGE_YEAR']:
      return getDateValueFrom_DATA_VALUE(DATA_VALUE, config.DATA_TYPE)
    default:
      return DATA_VALUE
  }
}

/**
 * ListSearch Nav_FormQuery 获取到 FilterGroup
 * @param value 
 * @param config 
 * @returns 
 */
export const getFilterGroupFrom_Nav_FormQuery = (value: any, config: any) => {
  const FilterGroup: FilterGroup = { Groups: [], Rules: [] }
  switch (config.DATA_TYPE) {
    // 多选下拉框
    case $consts['TABLE/DATA_TYPE_ENUM_MULTI_SELECT']:
      {
        const group: any = { IsAnd: false, Groups: [], Rules: [] }
        value.forEach((item: any, index: number) => {
          group.Rules.push({
            Field: config.QUERY_NAME,
            Operate: $consts['TABLE/OPERATE_TYPE_EQUAL'],
            Value: item,
            IsCustom: config.IS_CUSTOM,
            IsSysParamRule: config.IS_SYS_PARAM
          })
        })
        FilterGroup.Groups!.push(group)
      }
      break
    // 时间控件类型
    case $consts['TABLE/DATA_TYPE_DATE_RANGE_DAY_SECOND']:
    case $consts['TABLE/DATA_TYPE_DATE_RANGE_DAY_MINUTE']:
    case $consts['TABLE/DATA_TYPE_DATE_DAY']:
    case $consts['TABLE/DATA_TYPE_DATE_RANGE_DAY']:
    case $consts['TABLE/DATA_TYPE_DATE_MONTH']:
    case $consts['TABLE/DATA_TYPE_DATE_RANGE_MONTH']:
    case $consts['TABLE/DATA_TYPE_DATE_YEAR']:
    case $consts['TABLE/DATA_TYPE_DATE_RANGE_YEAR']:
      if (config.IS_CUSTOM) {
        FilterGroup.Rules!.push({
          Field: config.QUERY_NAME,
          Operate: $consts['TABLE/OPERATE_TYPE_ZERO'],
          Value: value,
          IsCustom: config.IS_CUSTOM,
          IsSysParamRule: config.IS_SYS_PARAM
        })
      } else {
        const group: any = { IsAnd: true, Groups: [], Rules: [] }
        const arr = value.split(',')
        arr.forEach((item: any, index: number) => {
          group.Rules.push({
            Field: config.QUERY_NAME,
            Operate: index ? $consts['TABLE/OPERATE_TYPE_LESS_EQUAL'] : $consts['TABLE/OPERATE_TYPE_GREATER_EQUAL'],
            Value: item,
            IsCustom: config.IS_CUSTOM,
            IsSysParamRule: config.IS_SYS_PARAM
          })
        })
        FilterGroup.Groups!.push(group)
      }
      break
    default:
      FilterGroup.Rules!.push({
        Field: config.QUERY_NAME,
        Operate: getDataTypeMatchOperate(config.DATA_TYPE),
        Value: value,
        IsCustom: config.IS_CUSTOM,
        IsSysParamRule: config.IS_SYS_PARAM
      })
  }
  return FilterGroup
}

/**
 * 分页下拉从导航字段获取过滤参数
 * @param navField INPUT_NAV_FIELD
 * @returns {Array} 导航字段参数
 */
export const getNavFieldsFromNavField = (navField: string) => {
  const navFields: string[] = []
  navField?.split(',').forEach(field => {
    const fields = field.split('.')
    if (fields.length > 1) {
      fields.splice(fields.length - 1, 1)
      navFields.push(fields.join('.'))
    }
  })
  return navFields
}

/**
 * 分页下拉从导航字段获取保存字段
 * @param val 分页下拉选中值
 * @param source 分页下拉数据源
 * @param config 分页下拉组件配置
 * @returns 
 */
export const getSaveFieldFromNavField = (val: string | number | undefined, source: any[], config: any) => {
  const extraFields: any = {}
  /**
   * FIELD_NAME: 配置保存的字段
   * INPUT_NAV_FIELD: 导航字段，比如 NAME，则从选中的项中取 NAME 字段的值，保存在保存字段中
   * INPUT_SAVE_FIELD: 保存字段，该字段通过 INPUT_NAV_FIELD 字段赋值后，一并提交给后端
   */
  const { INPUT_NAV_FIELD: nav, INPUT_SAVE_FIELD: save } = config
  if (nav && save) {
    const target: any = source.find((item: any) => item.ID === val)
    const navs = nav.split(',')
    const saves = save.split(',')
    navs.forEach((item: any, index: number) => {
      extraFields[saves[index]] = get(target, item)
    })
  }
  return extraFields
}

/**
 * 获取格式化配置对应参数值
 * @param format 格式化配置，比如 @record.CODE
 * @param {Object} data 数据源
 * @param {Object} data.user userinfo 数据
 * @param {Object} data.head 当前表单数据
 * @param {Object} data.record 列表选中行 record 数据
 * @returns 
 */
export const getFormatValue = (
  format: any,
  data: { user?: any, formBase?: any, record?: any, node?: any } = {}
) => {
  // 配置 @{user|head|record}.FIELD 的值
  const prefix = format?.toString().match(/^@(.+?)\.(.*?)$/)
  switch (prefix?.[1]) {
    // @user.NAME 取 user.NAME 的值 
    case 'user':
      format = get(data?.user, prefix[2])
      break
    // @head.NAME 取当前表单 NAME 字段的值
    case 'head':
      format = get(data?.formBase, prefix[2])
      break
    // @record.NAME 取列表选中行 record 的 NAME 字段的值
    case 'record':
      format = get(data?.record, prefix[2])
      break
    // @node.ID 取选中树节点的 ID 字段的值
    case 'node':
      format = get(data?.node, prefix[2])
      break
  }
  // 配置 @now @nowTime @nowDate 的值
  return format
}

/**
 * 按 DEFAULT_VALUE 配置格式化出对应的默认值
 * @param DEFAULT_VALUE 
 * @param data 
 * @returns 
 */
export const getDefaultValueFrom_DEFAULT_VALUE = (
  DEFAULT_VALUE: any,
  data: { user?: any, formBase?: any, record?: any, node?: any } = {}
) => {
  return getFormatValue(DEFAULT_VALUE, data)
}

/**
 * 根据格式化参数配置获取对应的参数值
 * @param query 比如 ENTRY_CODE=@record.CODE
 * @param data 数据源
 * @returns 
 */
export const getParamsFromQueryFormat = (
  query: string,
  data: { user?: any, formBase?: any, record?: any, node?: any } = {}
) => {
  const [field, format] = query.split('=')
  const formatValue = getFormatValue(format, data)
  return formatValue ? { [field]: formatValue } : null
}

/**
 * 通过配置的 Nav_Filters 过滤字段获得过滤参数 Rules
 * @param {Array} Nav_Filters 
 * @param {Object} data 数据源
 * @param {Object} data.user userinfo 数据
 * @param {Object} data.head 当前表单数据
 * @param {Object} data.record 列表选中行 record 数据
 * @returns 
 */
export const getRulesFromNavFilters = (
  Nav_Filters: any[] = [],
  data: { user?: any, formBase?: any, record?: any } = {}
) => {
  const rules: any = []
  Nav_Filters?.length && Nav_Filters.forEach(item => {
    const value = getDefaultValueFrom_DEFAULT_VALUE(item.VALUE, data)
    value !== undefined && rules.push(getSingleRule(item.FIELD_NAME, item.OPERATION, value, item.IS_CUSTOM, item.IS_SYS_PARAM))
  })
  return rules
}

/**
 * 编辑表单 form 校验域
 * @param config 配置项配置
 * @param value 配置项值
 * @returns 
 */
export const controlFieldVerify = (config: any, value: any): { invalid: boolean, msg?: string } => {
  const { REQUIRED, MAX_LEN, MIN_LEN, VERIFY_TYPE, VERIFY_EXPRESS, VERIFY_MSG } = config
  // 校验必填项
  if (REQUIRED) {
    const invalid = isEmpty(value)
    if (invalid) return { invalid, msg: VERIFY_MSG || '必填项' }
  }
  // 常见检验项
  if (VERIFY_TYPE) {
    let invalid = false
    switch (VERIFY_TYPE) {
      case 1:
        invalid = !(new RegExp('^$|^(-?\\d+)(\\.\\d+)?$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入数值' }
        break
      case 2:
        invalid = !(new RegExp('^$|^1\\d{10}$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入手机号' }
        break
      case 3:
        invalid = !(new RegExp('^$|^(\\d{4}-|\\d{3}-)?(\\d{8}|\\d{7})$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入固定电话' }
        break
      case 4:
        invalid = !(new RegExp('^$|^\\d{15}(\\d\\d[0-9xX])?$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入身份证号' }
        break
      case 5:
        invalid = !(new RegExp('^$|^\\w+([-+.]\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入电子邮件' }
        break
      case 6:
        invalid = !(new RegExp('^$|^[0-9a-fA-F]$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入十六进制' }
        break
      case 7:
        invalid = !(new RegExp('^$|^(\\d{4}|\\d{2})-((1[0-2])|(0?[1-9]))-(([12][0-9])|(3[01])|(0?[1-9]))$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入日期' }
        break
      case 8:
        invalid = !(new RegExp('^$|^[\\u4e00-\\u9fa5],{0,}$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入中文' }
        break
      case 9:
        invalid = !(new RegExp('^$|^[A-Za-z0-9]+$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入英文字符串' }
        break
      case 10:
        invalid = !(new RegExp('^$|^\\w+$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入英文字符串含符号' }
        break
      case 11:
        // eslint-disable-next-line no-useless-escape
        invalid = !(new RegExp('^((https?|ftp|smtp):\/\/)?(www.)?[a-z0-9]+\.[a-z]+(\/[a-zA-Z0-9#]+\/?)*$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入网址' }
        break
      case 12:
        // eslint-disable-next-line no-useless-escape
        invalid = !(new RegExp('^$|^((2[0-4]\\d|25[0-5]|[01]?\\d\d?)\\.){3}(2[0-4]\\d|25[0-5]|[01]?\\d\\d?)$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入IP地址' }
        break
      case 13:
        invalid = !(new RegExp('^$|^\\d+$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入非负整数' }
        break
      case 14:
        invalid = !(new RegExp('^$|[0-9]*[1-9][0-9]*$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入正整数' }
        break
      case 15:
        invalid = !(new RegExp('^$|^-[0-9]*[1-9][0-9]*$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入负整数' }
        break
      case 16:
        invalid = !(new RegExp('^$|^-?\\d+$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入整数' }
        break
      case 17:
        invalid = !(new RegExp('^$|^(-?\\d+)(\\.\\d+)?$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入小数' }
        break
      case 18:
        invalid = !(new RegExp('^$|^(([0-9]+\\.[0-9]*[1-9][0-9]*)|([0-9]*[1-9][0-9]*\\.[0-9]+)|([0-9]*[1-9][0-9]*))$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入正实数' }
        break
      case 19:
        invalid = !(new RegExp('^$|^(-(([0-9]+\\.[0-9]*[1-9][0-9]*)|([0-9]*[1-9][0-9]*\\.[0-9]+)|([0-9]*[1-9][0-9]*)))$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入负实数' }
        break
      case 20:
        invalid = !(new RegExp('^$|^(-?\\d+)(\\.\\d+)?$').test(value))
        if (invalid) return { invalid, msg: VERIFY_MSG || '请输入实数' }
        break
    }
  }
  // 配置了正则，则校验正则表达式
  if (VERIFY_EXPRESS) {
    const invalid = !(new RegExp(VERIFY_EXPRESS).test(value))
    if (invalid) return { invalid, msg: VERIFY_MSG || '格式错误' }
  }
  // 配置了最大长度
  if (MAX_LEN && String(value).length > MAX_LEN) return { invalid: true, msg: `超出${MAX_LEN}个字符` }
  // 配置了最小长度
  if (MIN_LEN && String(value).length < MIN_LEN) return { invalid: true, msg: `少于${MAX_LEN}个字符` }
  return { invalid: false }
}

/**
 * 编辑表单子表的导航属性获取
 * @param subPages 编辑表单子表
 * @returns 
 */
export const getEditSubPageNavFields = (subPages: any[] = []) => {
  const navFields: string[] = []
  subPages.forEach((item: any) => {
    const { NavFields, Nav_Columns = [] } = item
    NavFields && navFields.push(NavFields)
    Nav_Columns.forEach((col: any) => {
      const { FIELD_NAME } = col
      const fields = FIELD_NAME.split('.')
      if (fields.length > 1) {
        fields.splice(fields.length - 1, 1)
        NavFields && fields.splice(0, 0, NavFields)
        navFields.push(fields.join('.'))
      }
    })
  })
  return navFields
}

/**
 * 根据按钮配置的条件，检查按钮是否显示
 * @param config 
 * @param data 
 * @param menuFormParameter 
 * @returns 
 */
export const checkCondition = (condition: string, data?: any, menuFormParameter?: any) => {
  let isShow = !condition
  if (condition) {
    // 按钮配置条件
    const conditionsOr = condition.split('|')
    const conditionsAnd = condition.split('&')
    // 只配置了一个条件
    if (conditionsOr.length === 1 && conditionsAnd.length === 1) {
      const fields = condition.split(',')
      const val = String(get(data, fields[0]))
      // 条件直接命中菜单参数，则直接显示
      if (condition === menuFormParameter) {
        isShow = true
      }
      // 1 等于
      else if (parseInt(fields[1], 10) === 1) {
        if (val === fields[2]) {
          isShow = true
        }
      }
      // 2 不等于
      else if (parseInt(fields[1], 10) === 2) {
        if (val !== fields[2]) {
          isShow = true
        }
      }
    }
    // 或条件
    else if (conditionsOr.length > 1) {
      for (let conditionOr of conditionsOr) {
        const fields = conditionOr.split(',')
        const val = String(get(data, fields[0]))
        // 条件直接命中菜单参数，则直接显示
        if (conditionOr === menuFormParameter) {
          isShow = true
          break
        }
        // 1 等于
        else if (parseInt(fields[1], 10) === 1) {
          if (val === fields[2]) {
            isShow = true
            break
          }
        }
        // 2 不等于
        else if (parseInt(fields[1], 10) === 2) {
          if (val !== fields[2]) {
            isShow = true
            break
          }
        }
      }
    }
    // 与条件
    else if (conditionsAnd.length > 1) {
      const showResults = []
      for (let conditionAnd of conditionsAnd) {
        const fields = conditionAnd.split(',')
        const val = String(get(data, fields[0]))
        // 条件直接命中菜单参数，则直接显示
        if (conditionAnd === menuFormParameter) {
          showResults.push(true)
        }
        // 1 等于
        else if (parseInt(fields[1], 10) === 1) {
          if (val === fields[2]) {
            showResults.push(true)
          }
        }
        // 2 不等于
        else if (parseInt(fields[1], 10) === 2) {
          if (val !== fields[2]) {
            showResults.push(true)
          }
        }
      }
      isShow = showResults.length === conditionsAnd.length
    }
  }
  return isShow
}

/**
 * 获取表单有效 Nav_Columns 配置
 * @param Nav_Columns 
 * @param filterFields 过滤的字段 
 * @returns 
 */
export const getValidNavColumns = (
  Nav_Columns: any[],
  filterFields: string[] = ['IS_DEFAULT']
) => {
  return Nav_Columns.filter((item: any) => {
    // 多个过滤字段都返回为 true 时命中过滤
    const filters = filterFields.map(field => item[field])
    return filters.indexOf(false) === -1
  })
}

/**
 * 获取有效配置按钮
 * @param btns 
 * @returns 
 */
export const getValidBtns = (btns: any[]) => {
  if (!Array.isArray(btns)) return []

  return btns.filter(btn => {
    return btn.ENABLE_STATUS === $consts['TABLE/ENABLE_STATUS_ENABLE']
  })
}

/**
 * 移除对象中内嵌的对象
 * @param data 
 */
export const omitObjectFromData = (data: { [field: string]: any }) => {
  const targetData: any = {}
  for (const [key, value] of Object.entries(data)) {
    if (Array.isArray(value) || typeof value !== 'object') {
      targetData[key] = value
    }
  }
  return targetData
}

/**
 * 生成适合 treeDataSimpleMode Tree 的数据
 * @param treeData 树数据
 * @param simpleData 一维树数据
 * @param parentNode 
 */
export function generateCascaderTreeData (treeData: any[], simpleData: any[]) {
  treeData.forEach((tree: any) => {
    const { Node, Children, IsLeaf } = tree
    const treeItem: any = {
      label: Node.NAME,
      value: Node.ID,
      isLeaf: IsLeaf
    }
    simpleData.push(treeItem)
    if (Array.isArray(Children) && Children.length) {
      set(treeItem, 'children', [])
      generateCascaderTreeData(Children, treeItem.children)
    }
  })
}

/**
 * 生成适合 treeDataSimpleMode Tree 的数据
 * @param treeData 树数据
 * @param simpleData 一维树数据
 * @param parentNode 
 */
export function generateSimpleTreeData (treeData: any[], simpleData: any[], parentNode?: any) {
  treeData.forEach((tree: any) => {
    const { Node, Children, IsLeaf } = tree
    simpleData.push({
      id: Node.ID,
      pId: parentNode?.ID || undefined,
      title: Node.NAME,
      value: Node.ID,
      isLeaf: IsLeaf
    })
    Array.isArray(Children) && Children.length && generateSimpleTreeData(Children, simpleData, Node)
  })
}

/**
 * 生成一维的菜单树
 * @param menus 菜单树
 * @param flatMenus 目标一维的菜单树
 */
export function generateFlatTreeData (menus: any[], flatMenus: any[]) {
  menus.forEach((menu: any) => {
    const { Node, Children } = menu
    flatMenus.push(cloneDeep(Node))
    Array.isArray(Children) && Children.length && generateFlatTreeData(Children, flatMenus)
  })
}

/**
 * 编辑表单控件根据配置的宽度，确定控件所占宽度
 * @param maxCols 
 * @param takeCols 
 * @returns 
 */
export function getModalColumnsSpan (maxCols: number, takeCols?: number) {
  maxCols = maxCols || $consts['EDIT/PAGE_COL_NUMBER']
  if (!takeCols) return 24 / maxCols
  let percent = takeCols / maxCols
  percent = percent > 1 ? 1 : percent
  return 24 * percent
}

/**
 * 编辑表单根据配置的列数，确定编辑弹窗宽度
 * @param cols 
 * @returns 
 */
export function getModalWidthFromColNum (cols?: number) {
  cols = cols || $consts['EDIT/PAGE_COL_NUMBER']
  switch (cols) {
    case 1:
      return 25
    case 2:
      return 45
    case 3:
      return 60
    default:
      return 100
  }
}